import { useState, useCallback, useRef, useEffect } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { layoutMeme, trialBadgeReserve } from "./memeLayout";
import { wouldMark } from "@/lib/trialMark";
import { ToolInfoSection } from "@/components/ToolInfoSection";

const TOOL_INFO_CONTENT = {
  what: `Adds custom top and bottom text to an image in the classic meme format, with text sizing and positioning handled automatically.`,
  how: `Upload your image, enter your top and bottom text, and download the finished meme.`,
  tip: `This works with static images (JPG, PNG, etc.) — video and GIF meme support (adding text to a moving file) is a genuinely different, bigger undertaking and isn't available here yet, though a fuller video-meme flow may come later. Long text wraps onto up to three lines and shrinks to fit. On a trial use the small “Created with FreeDailyPro.com” badge sits in the top-right and bottom-right corners, so the captions are kept a little away from the top and bottom edges to stay clear of it. Keep meme text short anyway — the classic format works because it's quick to read at a glance, and long paragraphs of text tend to undercut the format rather than fit into it.`,
};

export default function MemeGenerator() {
  const [file, setFile] = useState<File | null>(null);
  const [topText, setTopText] = useState("TOP TEXT");
  const [bottomText, setBottomText] = useState("BOTTOM TEXT");
  const topTextRef = useRef(topText);
  const bottomTextRef = useRef(bottomText);
  topTextRef.current = topText; bottomTextRef.current = bottomText;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    // Load the meme font, then redraw (canvas text uses the fallback until the font is there).
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Anton&display=swap";
    document.head.appendChild(link);
    document.fonts?.load("40px Anton").then(() => setFontReady(true)).catch(() => {});
    return () => { document.head.removeChild(link); };
  }, []);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f);
    const img = new Image();
    img.onload = () => { imgRef.current = img; draw(); };
    img.src = URL.createObjectURL(f);
  }, []);

  const draw = (top = topTextRef.current, bottom = bottomTextRef.current) => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0);
    // Anton is loaded from Google Fonts (Impact doesn't exist on Android/iOS); Impact and Arial Black are the fallbacks.
    const fontAt = (px: number) => `${px}px Anton, Impact, "Arial Black", "Haettenschweiler", sans-serif`;
    const measureAt = (px: number) => (t: string) => { ctx.font = fontAt(px); return ctx.measureText(t).width; };
    const reserve = wouldMark("image/png", "meme.png") ? trialBadgeReserve(canvas.width, canvas.height) : 0;
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; ctx.lineJoin = "round";
    ctx.fillStyle = "#fff"; ctx.strokeStyle = "#000";
    for (const l of layoutMeme(top, bottom, canvas.width, canvas.height, measureAt, reserve)) {
      ctx.font = fontAt(l.size); ctx.lineWidth = l.strokeWidth;
      ctx.strokeText(l.text, l.x, l.y);
      ctx.fillText(l.text, l.x, l.y);
    }
  };

  useEffect(() => { draw(); }, [topText, bottomText, fontReady]);

  const download = () => { canvasRef.current?.toBlob(blob => { if (blob) downloadBlob(blob, "meme.png"); }); };

  const clearAll = () => {
    setFile(null);
    imgRef.current = null;
    setTopText("TOP TEXT");
    setBottomText("BOTTOM TEXT");
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image for your meme" description="Add top and bottom text" />
      ) : (
        <div className="space-y-4">
          <canvas ref={canvasRef} className="max-h-64 mx-auto rounded-lg border" />
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Top Text</Label><Input aria-label="Top Text" value={topText} onChange={e => setTopText(e.target.value)} /></div>
            <div><Label>Bottom Text</Label><Input aria-label="Bottom Text" value={bottomText} onChange={e => setBottomText(e.target.value)} /></div>
          </div>
          <div className="flex gap-3">
            <DownloadButton onClick={download} markHint="meme.png" label="Download Meme" />
            <Button variant="outline" onClick={clearAll} className="gap-1.5">
              <X className="h-4 w-4" /> Clear
            </Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
